import { ReactElement, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";

import styles from "./styles/_BlockTitleCS.module.scss";
import classNames from "classnames";

import { observer } from "mobx-react";
import EditableField from "../../utilities/EditableField";
import WatsonApi from "../../../backends/WatsonApi";
import NotificationManager from "../../notifications/NotificationManager";
import { runInAction } from "mobx";
import ViewMoreButton from "./ViewMoreButton";
import { EditDropDownItem } from "./EditDropDown";
import dynamic from "next/dynamic";
import { useStores } from "../../../stores/useStores";

const EditDropDown = dynamic(() => import("./EditDropDown"));

const blockTitleClassName = classNames({
  [styles.blockTitle]: true,
});

const blockTitlePlaceholderClassName = classNames({
  [styles.blockTitle]: true,
  [styles.blockTitlePlaceholder]: true,
});

const transitionClassNames = {
  enter: styles.transitionEnter,
  enterActive: styles.transitionEnterActive,
  enterDone: styles.transitionEnterDone,
  exit: styles.transitionExit,
  exitActive: styles.transitionExitActive,
  exitDone: styles.transitionExitDone,
};

type IBlockTitleCSProps = {
  aboveTheFold: boolean;
  allowSave?: boolean;
  customTitleComponent?: ReactElement<any, any>;
  blockTitleContainerClassName?: string;
  editDropDownClassName?: string;
  dropDownItems?: EditDropDownItem[];
  showViewMore: boolean;
  viewMoreButtonText?: string | ReactElement;
  viewMoreButtonUrl?: string | undefined;
  rightElement?: ReactElement<any, any>;
  rightElementClassName?: string;
} & (
  | {
      blockObj:
        | IFilesTerraceBlock
        | IAboutTerrace
        | IHTmlTerrace
        | ILinksTerrace
        | IStatementTerrace
        | IShortcutsTerrace
        | IAccordionTerrace
        | IShopTerrace;
    }
  | {
      blockObj: ITerrace;
    }
);

const BlockTitleCS = observer((props: IBlockTitleCSProps) => {
  const { userStore, gridStore, organizationStore } = useStores();
  const [title, setTitle] = useState(props.blockObj.title);
  const [isEditing, setIsEditing] = useState(false);
  const transitionRef = useRef(null);

  const blockTitleContainerClassName = classNames({
    [styles.blockTitleContainer]: true,
    [styles.blockTitleContainerAboveTheFold]: props.aboveTheFold,
    [props.blockTitleContainerClassName || ""]:
      props.blockTitleContainerClassName,
  });

  const titleWrapperClassName = classNames({
    [styles.titleWrapper]: true,
  });

  const titleContainerClassName = classNames({
    [styles.titleContainer]: true,
    [styles.titleContainerHasRightElement]: !!props.rightElement,
  });

  async function handleSave(newTitle: string) {
    if (newTitle !== props.blockObj.title) {
      const originalTitle = title;
      try {
        const client = await WatsonApi();
        const response =
          await client.apis.organizations.organizations_terraces_partial_update(
            {
              organization_pk: props.blockObj.organization_id,
              id: props.blockObj.id,
              data: {
                title: newTitle,
              },
            },
          );
        NotificationManager.success("Title successfully saved!");
        runInAction(() => {
          gridStore.blocks.set(props.blockObj.id, JSON.parse(response.data));
        });
        setTitle(newTitle);
        setIsEditing(false);
      } catch {
        setTitle(originalTitle);
        NotificationManager.error("Something went wrong! Please try again.");
      }
    } else {
      setIsEditing(false);
    }
  }

  function handleCancel() {
    setIsEditing(false);
  }

  return (
    <div className={styles.titleTransitionContainer}>
      {(props.blockObj.title || userStore.isEditor) && (
        <CSSTransition
          nodeRef={transitionRef}
          appear
          classNames={transitionClassNames}
          in={props.aboveTheFold}
          timeout={250}
        >
          <header ref={transitionRef} className={blockTitleContainerClassName}>
            <div className={styles.innerTitleContainer}>
              <div className={titleContainerClassName}>
                <div className={styles.title}>
                  {userStore.isEditor && (
                    <EditDropDown
                      className={props.editDropDownClassName}
                      blockObj={props.blockObj}
                      title={title}
                      dropDownItems={props.dropDownItems}
                    />
                  )}
                  {props.customTitleComponent ? (
                    <div className={titleWrapperClassName}>
                      {props.customTitleComponent}
                    </div>
                  ) : (
                    <div className={titleWrapperClassName}>
                      <EditableField
                        defaultValue={props.blockObj.title}
                        isEditing={isEditing}
                        onEditStart={() => setIsEditing(true)}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        canEdit={userStore.isEditor}
                        placeholder="Enter a Title"
                        className={styles.editableTitle}
                      >
                        {userStore.isEditor && !props.blockObj.title ? (
                          <h2 className={blockTitlePlaceholderClassName}>
                            Add Title
                          </h2>
                        ) : (
                          <h2 className={blockTitleClassName}>
                            {props.blockObj.title}
                          </h2>
                        )}
                      </EditableField>
                    </div>
                  )}
                </div>
                {props.showViewMore && (
                  <div className={styles.viewMoreButton}>
                    <ViewMoreButton
                      terraceType={props.blockObj.type}
                      organizationId={organizationStore.currentOrganization.id}
                      text={props.viewMoreButtonText}
                      url={props.viewMoreButtonUrl}
                    />
                  </div>
                )}
                {props.rightElement && (
                  <div className={props.rightElementClassName}>
                    {props.rightElement}
                  </div>
                )}
              </div>
            </div>
          </header>
        </CSSTransition>
      )}
    </div>
  );
});

export default BlockTitleCS;
